import { Period, PeriodSelector } from '@/components/dashboard/period-selector';
import type { ChartPeriod } from '@/components/dashboard/chart/chart-utils';
import { RevenueChart } from '@/components/dashboard/revenue-chart';
import {
  ComparativeRevenueChart,
  type ComparativeDataPoint,
} from '@/components/dashboard/comparative-revenue-chart';
import { TransactionList } from '@/components/dashboard/transaction-list';
import { MetricCard } from '@/components/dashboard/metric-card';
import { FadeInView } from '@/components/ui/fade-in-view';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { ThemedText } from '@/components/themed-text';
import { useFinancialSummary } from '@/hooks/use-financial-summary';
import { useOperationalMetrics } from '@/hooks/use-provider';
import { useRecentCharges } from '@/hooks/use-recent-charges';
import { useTerms } from '@/hooks/use-terms';
import { useExpenses, useExpensesTotal } from '@/hooks/use-expenses';
import { CHARGE_AMOUNTS } from '@/content/payments/charge';
import { CHARGE_STATUS } from '@/types/charge-status';
import { formatCurrency } from '@/utils/format-currency';
import { Ionicons } from '@expo/vector-icons';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { Pressable, ScrollView, View, useWindowDimensions } from 'react-native';
import { PageLayout } from '@/components/ui/page-layout';
import { PressableScale } from '@/components/ui/pressable-scale';
import { TeamReportView } from '@/components/dashboard/team-report';
import { getFirstName } from '@/components/team/member-avatar';
import { useMembership } from '@/hooks/use-membership';
import { switchActingProvider, useActingProviderStore } from '@/store/actingProviderStore';
import { TeamReportMember } from '@/types/organization';
import { useThemedHex } from '@/hooks/use-theme-colors';

const MOBILE_BREAKPOINT = 600;

/** "Eu" é quem está em foco; "Equipe" é a organização inteira. */
type Scope = 'mine' | 'team';

export default function DashboardScreen() {
  const th = useThemedHex();
  const { width } = useWindowDimensions();
  const isMobile = width < MOBILE_BREAKPOINT;
  const { can } = useMembership();
  const acting = useActingProviderStore((s) => s.acting);
  const [scope, setScope] = useState<Scope>('mine');
  const showTeam = can('teamReports');
  const inTeam = showTeam && scope === 'team';

  // Linha da equipe: o foco vai para a pessoa e a tela volta para a visão dela.
  const openMember = (member: TeamReportMember) => {
    switchActingProvider(
      member.role === 'OWNER'
        ? null
        : {
            providerId: member.providerId,
            name: member.name,
            image: member.image,
            color: member.color,
          },
    );
    setScope('mine');
  };

  // State
  const [period, setPeriod] = useState<Period>('30d');
  // Períodos longos/abertos caem em 90d; mês/mês seguinte tratam como 30d.
  const chartPeriod: ChartPeriod =
    period === '7d'
      ? '7d'
      : period === '90d' || period === 'all' || period === 'custom'
        ? '90d'
        : '30d';
  const [customRange, setCustomRange] = useState<{ start: Date; end: Date }>({
    start: subDays(new Date(), 30),
    end: new Date(),
  });
  const [selectedMetric, setSelectedMetric] = useState<
    'comparative' | 'revenue' | 'pending' | 'expenses' | 'clients' | 'contracts'
  >('comparative');

  // Queries
  // Fetch a larger number of charges to support "General" view better
  const { data: recentCharges, isLoading: isLoadingCharges } = useRecentCharges(1000);
  const { data: financialSummary, isLoading: isLoadingFinancial } = useFinancialSummary();
  const terms = useTerms();
  const { data: metrics, isLoading: isLoadingMetrics } = useOperationalMetrics();

  // Period bounds for expenses and revenue-in-period
  const periodBounds = useMemo(() => {
    let start: Date;
    let end: Date = endOfDay(new Date());
    if (period === 'custom') {
      start = startOfDay(customRange.start);
      end = endOfDay(customRange.end);
    } else if (period === 'all') {
      start = subDays(new Date(), 365);
      end = endOfDay(new Date());
    } else {
      const days = period === '7d' ? 7 : period === '30d' ? 30 : 90;
      end = endOfDay(new Date());
      start = startOfDay(subDays(end, days - 1));
    }
    return {
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd'),
    };
  }, [period, customRange]);

  const { data: expensesTotal } = useExpensesTotal(periodBounds.startDate, periodBounds.endDate);
  const { data: expensesList } = useExpenses({
    startDate: periodBounds.startDate,
    endDate: periodBounds.endDate,
    page: 1,
    limit: 500,
  });
  const expensesInPeriod = useMemo(() => expensesList?.data ?? [], [expensesList?.data]);

  // `fee` só soma cobranças automáticas já liquidadas (`feeAmount` vem do
  // webhook, T6.3) — a taxa do Stripe não volta num reembolso (plano, seção
  // 4.5), por isso não é descontada quando o status é REFUNDED.
  const { revenue: revenueInPeriod, fee: feeInPeriod } = useMemo(() => {
    if (!recentCharges) return { revenue: 0, fee: 0 };
    const start = startOfDay(new Date(periodBounds.startDate));
    const end = endOfDay(new Date(periodBounds.endDate));
    return recentCharges.reduce(
      (acc, charge) => {
        if (charge.status !== CHARGE_STATUS.PAID && charge.status !== CHARGE_STATUS.REFUNDED)
          return acc;
        const refDate = charge.paidAt || charge.createdAt;
        if (!refDate) return acc;
        const d = new Date(refDate);
        if (d < start || d > end) return acc;
        const amount =
          typeof charge.amount === 'string' ? parseFloat(charge.amount) : charge.amount;
        if (Number.isNaN(amount)) return acc;
        const fee =
          typeof charge.feeAmount === 'string' ? parseFloat(charge.feeAmount) : charge.feeAmount;
        const feeAdd = fee != null && !Number.isNaN(fee) ? fee : 0;
        if (charge.status === CHARGE_STATUS.REFUNDED) {
          const refunded =
            typeof charge.refundedAmount === 'string'
              ? parseFloat(charge.refundedAmount)
              : (charge.refundedAmount ?? 0);
          return { revenue: acc.revenue + Math.max(amount - refunded, 0), fee: acc.fee + feeAdd };
        }
        return { revenue: acc.revenue + amount, fee: acc.fee + feeAdd };
      },
      { revenue: 0, fee: 0 },
    );
  }, [recentCharges, periodBounds.startDate, periodBounds.endDate]);

  /** Só aparece "Taxas"/"Líquido" no hero quando houve cobrança automática liquidada no período (T6.5). */
  const hasAutomaticFeesInPeriod = feeInPeriod > 0;
  const netRevenueInPeriod = Math.max(revenueInPeriod - feeInPeriod, 0);

  // Taxa do Stripe já saiu do dinheiro do prestador (não volta com reembolso,
  // ver acima) — por isso entra no lucro como custo real, não só na leitura
  // "Taxa"/"Líquido" do hero. Sem cobrança automática, `feeInPeriod` é 0 e o
  // lucro não muda.
  const profitInPeriod = (revenueInPeriod ?? 0) - (feeInPeriod ?? 0) - (expensesTotal ?? 0);

  // Dados comparativos (recebidos x gastos x lucro) por dia — só no intervalo em que há dados
  const comparativeChartData = useMemo((): ComparativeDataPoint[] => {
    let periodStart: Date;
    let periodEnd: Date = endOfDay(new Date());
    if (period === 'custom') {
      periodStart = startOfDay(customRange.start);
      periodEnd = endOfDay(customRange.end);
    } else if (period === 'all') {
      periodStart = subDays(new Date(), 365);
      periodEnd = endOfDay(new Date());
    } else {
      const days = period === '7d' ? 7 : period === '30d' ? 30 : 90;
      periodEnd = endOfDay(new Date());
      periodStart = startOfDay(subDays(periodEnd, days - 1));
    }

    const revenueByDay: Record<string, number> = {};
    const feeByDay: Record<string, number> = {};
    const expensesByDay: Record<string, number> = {};

    (recentCharges || []).forEach((charge) => {
      if (charge.status !== CHARGE_STATUS.PAID && charge.status !== CHARGE_STATUS.REFUNDED) return;
      const refDate = charge.paidAt || charge.createdAt;
      if (!refDate) return;
      const d = new Date(refDate);
      if (d < periodStart || d > periodEnd) return;
      const key = format(d, 'yyyy-MM-dd');
      const amount = typeof charge.amount === 'string' ? parseFloat(charge.amount) : charge.amount;
      if (Number.isNaN(amount)) return;
      // Mesma regra do hero: a taxa não volta com o reembolso, então conta
      // inteira mesmo em REFUNDED.
      const fee =
        typeof charge.feeAmount === 'string' ? parseFloat(charge.feeAmount) : charge.feeAmount;
      if (fee != null && !Number.isNaN(fee)) {
        feeByDay[key] = (feeByDay[key] ?? 0) + fee;
      }
      if (charge.status === CHARGE_STATUS.REFUNDED) {
        const refunded =
          typeof charge.refundedAmount === 'string'
            ? parseFloat(charge.refundedAmount)
            : (charge.refundedAmount ?? 0);
        revenueByDay[key] = (revenueByDay[key] ?? 0) + Math.max(amount - refunded, 0);
      } else {
        revenueByDay[key] = (revenueByDay[key] ?? 0) + amount;
      }
    });

    expensesInPeriod.forEach((expense) => {
      const d = new Date(expense.date);
      if (d < periodStart || d > periodEnd) return;
      const key = format(d, 'yyyy-MM-dd');
      const amount =
        typeof expense.amount === 'string' ? parseFloat(expense.amount) : expense.amount;
      expensesByDay[key] = (expensesByDay[key] ?? 0) + amount;
    });

    const allKeys = new Set(
      [...Object.keys(revenueByDay), ...Object.keys(expensesByDay)].filter(
        (key) => (revenueByDay[key] ?? 0) > 0 || (expensesByDay[key] ?? 0) > 0,
      ),
    );

    if (allKeys.size === 0) return [];

    const sortedKeys = Array.from(allKeys).sort();
    const dataStart = startOfDay(new Date(sortedKeys[0]));
    const dataEnd = endOfDay(new Date(sortedKeys[sortedKeys.length - 1]));
    const daysDiff =
      Math.ceil((dataEnd.getTime() - dataStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const renderDays = Math.min(Math.max(daysDiff, 1), 365);

    return Array.from({ length: renderDays }, (_, idx) => {
      const date = new Date(dataStart);
      date.setDate(date.getDate() + idx);
      const key = format(date, 'yyyy-MM-dd');
      const revenue = revenueByDay[key] ?? 0;
      const expenses = expensesByDay[key] ?? 0;
      const fee = feeByDay[key] ?? 0;
      const profit = revenue - fee - expenses;
      return {
        date: date.toISOString(),
        revenue,
        expenses,
        profit,
      };
    });
  }, [period, customRange, recentCharges, expensesInPeriod]);

  // Derived Data
  const chartData = useMemo(() => {
    let startDate: Date;
    let endDate: Date = new Date();

    if (period === 'custom') {
      startDate = startOfDay(customRange.start);
      endDate = endOfDay(customRange.end);
    } else if (period === 'all') {
      // For "all", we find the earliest charge date or default to 1 year
      if (recentCharges && recentCharges.length > 0) {
        const earliestTimestamp = recentCharges.reduce((min, c) => {
          const cDate = new Date(c.createdAt).getTime();
          return cDate < min ? cDate : min;
        }, new Date().getTime());
        startDate = startOfDay(new Date(earliestTimestamp));
      } else {
        startDate = subDays(new Date(), 365);
      }
      endDate = endOfDay(new Date());
    } else {
      const days = period === '7d' ? 7 : period === '30d' ? 30 : 90;
      endDate = endOfDay(new Date());
      startDate = startOfDay(subDays(endDate, days - 1));
    }

    const dataByDay: Record<string, number | Set<string>> = {};

    if (selectedMetric === 'expenses') {
      expensesInPeriod.forEach((expense) => {
        const parsedDate = new Date(expense.date);
        if (parsedDate < startDate || parsedDate > endDate) return;
        const key = format(parsedDate, 'yyyy-MM-dd');
        const amount =
          typeof expense.amount === 'string' ? parseFloat(expense.amount) : expense.amount;
        dataByDay[key] = ((dataByDay[key] as number) || 0) + amount;
      });
    } else {
      (recentCharges || []).forEach((charge) => {
        // Determine date based on metric
        let referenceDate: string | Date | undefined;

        if (selectedMetric === 'pending') {
          referenceDate = charge.dueDate || charge.createdAt;
        } else if (selectedMetric === 'revenue') {
          referenceDate = charge.paidAt || charge.createdAt;
        } else {
          referenceDate = charge.createdAt;
        }

        if (!referenceDate) return;

        const parsedDate = new Date(referenceDate);

        // Filter by date range
        if (parsedDate < startDate || parsedDate > endDate) return;

        const key = format(parsedDate, 'yyyy-MM-dd');
        const amount =
          typeof charge.amount === 'string' ? parseFloat(charge.amount) : charge.amount;

        if (selectedMetric === 'revenue') {
          const isPaid = charge.status === CHARGE_STATUS.PAID;
          const isRefunded = charge.status === CHARGE_STATUS.REFUNDED;

          if (isPaid || isRefunded) {
            const refundedAmount =
              typeof charge.refundedAmount === 'string'
                ? parseFloat(charge.refundedAmount)
                : charge.refundedAmount || 0;
            const netAmount = isRefunded ? Math.max(amount - refundedAmount, 0) : amount;

            if (netAmount > 0) {
              dataByDay[key] = ((dataByDay[key] as number) || 0) + netAmount;
            }
          }
        } else if (selectedMetric === 'pending') {
          const isValidStatus =
            charge.status === CHARGE_STATUS.PENDING ||
            charge.status === CHARGE_STATUS.OVERDUE ||
            charge.status === CHARGE_STATUS.IN_DISPUTE;
          if (isValidStatus) {
            dataByDay[key] = ((dataByDay[key] as number) || 0) + amount;
          }
        } else if (selectedMetric === 'clients') {
          if (!dataByDay[key]) dataByDay[key] = new Set<string>();
          const clientId =
            charge.clientId ||
            (charge.enrollment as { client?: { id: string }; clientId?: string })?.client?.id ||
            (charge.enrollment as { clientId?: string })?.clientId ||
            charge.enrollmentId ||
            charge.id;
          (dataByDay[key] as Set<string>).add(clientId);
        } else if (selectedMetric === 'contracts') {
          if (!dataByDay[key]) dataByDay[key] = new Set<string>();
          if (charge.enrollmentId) (dataByDay[key] as Set<string>).add(charge.enrollmentId);
        }
      });
    }

    const keysWithData = Object.keys(dataByDay).filter((key) => {
      const v = dataByDay[key];
      if (selectedMetric === 'clients' || selectedMetric === 'contracts') {
        return (v as Set<string>)?.size > 0;
      }
      return (v as number) > 0;
    });
    if (keysWithData.length === 0) return [];

    const sortedKeys = keysWithData.sort();
    const dataStart = startOfDay(new Date(sortedKeys[0]));
    const dataEnd = endOfDay(new Date(sortedKeys[sortedKeys.length - 1]));
    const actualDaysDiff =
      Math.ceil((dataEnd.getTime() - dataStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const renderDays = Math.min(Math.max(actualDaysDiff, 1), 365);

    return Array.from({ length: renderDays }, (_, idx) => {
      const date = new Date(dataStart);
      date.setDate(date.getDate() + idx);
      const key = format(date, 'yyyy-MM-dd');

      let value = 0;
      if (selectedMetric === 'clients' || selectedMetric === 'contracts') {
        value = (dataByDay[key] as Set<string>)?.size || 0;
      } else {
        value = (dataByDay[key] as number) || 0;
      }

      return { date: date.toISOString(), value };
    });
  }, [period, recentCharges, customRange, selectedMetric, expensesInPeriod]);

  const hasAnalyticsData =
    !!financialSummary ||
    !!metrics ||
    (recentCharges && recentCharges.length > 0) ||
    chartData.some((item) => item.value > 0) ||
    comparativeChartData.some((d) => d.revenue > 0 || d.expenses > 0);

  const getMetricTitle = () => {
    switch (selectedMetric) {
      case 'comparative':
        return 'Recebidos x Gastos x Lucro';
      case 'revenue':
        return 'Recebido';
      case 'pending':
        return 'A Receber';
      case 'expenses':
        return 'Gastos';
      case 'clients':
        return `${terms.title.clientPlural} ${terms.agree('Ativos', 'client')}`;
      case 'contracts':
        return `${terms.title.enrollmentPlural} ${terms.agree('Ativos', 'enrollment')}`;
      default:
        return 'Dados';
    }
  };

  const chartMetricOptions = [
    { id: 'comparative' as const, label: 'Comparativo', icon: 'bar-chart-outline' as const },
    { id: 'revenue' as const, label: 'Recebido', icon: 'trending-up-outline' as const },
    { id: 'pending' as const, label: 'A receber', icon: 'time-outline' as const },
    { id: 'expenses' as const, label: 'Gastos', icon: 'wallet-outline' as const },
    { id: 'clients' as const, label: terms.title.clientPlural, icon: 'people-outline' as const },
    {
      id: 'contracts' as const,
      label: terms.title.enrollmentPlural,
      icon: 'document-text-outline' as const,
    },
  ];

  return (
    <PageLayout title="Relatórios" subtitle="A saúde financeira do seu negócio">
      {showTeam && (
        <View className="flex-row self-start rounded-full bg-neutral-200/70 p-0.5">
          {(['mine', 'team'] as Scope[]).map((s) => {
            const active = scope === s;
            const label = s === 'team' ? 'Equipe' : acting ? getFirstName(acting.name) : 'Eu';
            return (
              <PressableScale
                key={s}
                haptic
                onPress={() => setScope(s)}
                accessibilityRole="tab"
                accessibilityState={{ selected: active }}
                className="rounded-full px-4 py-1.5"
                style={{ backgroundColor: active ? th('#fff') : 'transparent' }}
              >
                <ThemedText
                  className="font-bold text-xs"
                  style={{ color: active ? '#FD5001' : th('#737373') }}
                >
                  {label}
                </ThemedText>
              </PressableScale>
            );
          })}
        </View>
      )}

      <PeriodSelector
        noCard
        label="Período"
        period={period}
        onPeriodChange={setPeriod}
        customRange={customRange}
        onCustomRangeChange={setCustomRange}
      />

      {inTeam ? (
        <TeamReportView
          from={periodBounds.startDate}
          to={periodBounds.endDate}
          onOpenMember={openMember}
        />
      ) : !hasAnalyticsData && !isLoadingFinancial && !isLoadingMetrics && !isLoadingCharges ? (
        <Card>
          <EmptyState
            icon="bar-chart-outline"
            title="Sem dados por enquanto"
            description="Assim que houver cobranças pagas, seus gráficos e métricas aparecem aqui."
            className="py-10"
          />
        </Card>
      ) : (
        <>
          {/* Hero: lucro no período */}
          <FadeInView delay={0}>
            <View
              className="w-full gap-5 rounded-3xl bg-ink p-5"
              style={{
                borderCurve: 'continuous',
                boxShadow: '0 12px 32px rgba(10, 10, 10, 0.25)',
              }}
            >
              <View className="flex-row items-center justify-between">
                <ThemedText className="font-medium text-sm text-white/60">
                  Lucro no período
                </ThemedText>
                <View
                  className={`flex-row items-center gap-1.5 rounded-full px-2.5 py-1 ${
                    profitInPeriod >= 0 ? 'bg-white/10' : 'bg-error-500/20'
                  }`}
                >
                  <Ionicons
                    name={profitInPeriod >= 0 ? 'trending-up' : 'trending-down'}
                    size={13}
                    color={profitInPeriod >= 0 ? '#4ADE80' : '#F87171'}
                  />
                  <ThemedText
                    className={`font-semibold text-xs ${
                      profitInPeriod >= 0 ? 'text-[#4ADE80]' : 'text-[#F87171]'
                    }`}
                  >
                    {profitInPeriod >= 0 ? 'Positivo' : 'Negativo'}
                  </ThemedText>
                </View>
              </View>

              <ThemedText
                className="font-bold text-4xl text-white"
                style={{ fontVariant: ['tabular-nums'] }}
                numberOfLines={1}
                adjustsFontSizeToFit
              >
                {formatCurrency(profitInPeriod)}
              </ThemedText>

              <View className="flex-row gap-3">
                <View className="flex-1 gap-1 rounded-2xl bg-white/10 p-3">
                  <View className="flex-row items-center gap-1.5">
                    <View className="h-1.5 w-1.5 rounded-full bg-[#4ADE80]" />
                    <ThemedText className="font-medium text-xs text-white/60">Recebido</ThemedText>
                  </View>
                  <ThemedText
                    className="font-semibold text-base text-white"
                    style={{ fontVariant: ['tabular-nums'] }}
                    numberOfLines={1}
                    adjustsFontSizeToFit
                  >
                    {formatCurrency(revenueInPeriod ?? 0)}
                  </ThemedText>
                </View>
                <View className="flex-1 gap-1 rounded-2xl bg-white/10 p-3">
                  <View className="flex-row items-center gap-1.5">
                    <View className="h-1.5 w-1.5 rounded-full bg-[#F87171]" />
                    <ThemedText className="font-medium text-xs text-white/60">Gastos</ThemedText>
                  </View>
                  <ThemedText
                    className="font-semibold text-base text-white"
                    style={{ fontVariant: ['tabular-nums'] }}
                    numberOfLines={1}
                    adjustsFontSizeToFit
                  >
                    {formatCurrency(expensesTotal ?? 0)}
                  </ThemedText>
                </View>
              </View>

              {/* Taxa numa linha própria: só com cobrança automática liquidada no período (T6.5, plano 4.5/6.4) */}
              {hasAutomaticFeesInPeriod && (
                <View className="flex-row gap-3">
                  <View className="flex-1 gap-1 rounded-2xl bg-white/10 p-3">
                    <View className="flex-row items-center gap-1.5">
                      <View className="h-1.5 w-1.5 rounded-full bg-white/40" />
                      <ThemedText className="font-medium text-xs text-white/60">
                        {CHARGE_AMOUNTS.feeLabel}
                      </ThemedText>
                    </View>
                    <ThemedText
                      className="font-semibold text-base text-white"
                      style={{ fontVariant: ['tabular-nums'] }}
                      numberOfLines={1}
                      adjustsFontSizeToFit
                    >
                      {formatCurrency(feeInPeriod)}
                    </ThemedText>
                  </View>
                  <View className="flex-1 gap-1 rounded-2xl bg-white/10 p-3">
                    <View className="flex-row items-center gap-1.5">
                      <View className="h-1.5 w-1.5 rounded-full bg-[#4ADE80]" />
                      <ThemedText className="font-medium text-xs text-white/60">
                        {CHARGE_AMOUNTS.netLabel}
                      </ThemedText>
                    </View>
                    <ThemedText
                      className="font-semibold text-base text-white"
                      style={{ fontVariant: ['tabular-nums'] }}
                      numberOfLines={1}
                      adjustsFontSizeToFit
                    >
                      {formatCurrency(netRevenueInPeriod)}
                    </ThemedText>
                  </View>
                </View>
              )}
            </View>
          </FadeInView>

          {/* Métricas do período */}
          <View className="flex-row flex-wrap gap-3">
            {financialSummary && (
              <FadeInView delay={80} className="min-w-[45%] flex-1">
                <MetricCard
                  title="A receber"
                  value={formatCurrency(financialSummary.balance.pending)}
                  icon="time-outline"
                  compact={isMobile}
                />
              </FadeInView>
            )}
            {metrics && (
              <>
                <FadeInView delay={140} className="min-w-[45%] flex-1">
                  <MetricCard
                    title="Receita do mês"
                    value={formatCurrency(metrics.monthlyRevenue)}
                    icon="cash-outline"
                    compact={isMobile}
                  />
                </FadeInView>
                <FadeInView delay={200} className="min-w-[45%] flex-1">
                  <MetricCard
                    title={terms.title.clientPlural}
                    value={String(metrics.clientMetrics.total)}
                    icon="people-outline"
                    compact={isMobile}
                    change={
                      metrics.clientMetrics.newThisMonth > 0
                        ? metrics.clientMetrics.newThisMonth
                        : undefined
                    }
                  />
                </FadeInView>
                <FadeInView delay={260} className="min-w-[45%] flex-1">
                  <MetricCard
                    title={`${terms.title.enrollmentPlural} ${terms.agree('ativos', 'enrollment')}`}
                    value={String(metrics.activeEnrollments)}
                    icon="document-text-outline"
                    compact={isMobile}
                    change={metrics.weeklyGrowth !== 0 ? metrics.weeklyGrowth : undefined}
                  />
                </FadeInView>
              </>
            )}
          </View>

          {/* Gráfico */}
          <FadeInView delay={320}>
            <Card>
              <CardHeader>
                <CardTitle>Evolução</CardTitle>
              </CardHeader>
              <CardContent className="gap-4">
                <ScrollView
                  keyboardShouldPersistTaps="handled"
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={{ gap: 8 }}
                >
                  {chartMetricOptions.map((item) => {
                    const isSelected = selectedMetric === item.id;
                    return (
                      <Pressable
                        key={item.id}
                        onPress={() => setSelectedMetric(item.id)}
                        accessibilityRole="button"
                        accessibilityState={{ selected: isSelected }}
                        className={`flex-row items-center gap-2 rounded-full px-4 py-2 active:opacity-80 ${
                          isSelected ? 'bg-primary' : 'bg-neutral-100'
                        }`}
                      >
                        <Ionicons
                          name={item.icon}
                          size={16}
                          color={isSelected ? '#FFFFFF' : th('#737373')}
                        />
                        <ThemedText
                          className={`font-semibold text-xs ${
                            isSelected ? 'text-white' : 'text-neutral-600'
                          }`}
                        >
                          {item.label}
                        </ThemedText>
                      </Pressable>
                    );
                  })}
                </ScrollView>

                {selectedMetric === 'comparative' ? (
                  <ComparativeRevenueChart data={comparativeChartData} period={chartPeriod} />
                ) : (
                  <RevenueChart
                    data={chartData}
                    period={chartPeriod}
                    title={getMetricTitle()}
                    type={
                      selectedMetric === 'clients' || selectedMetric === 'contracts'
                        ? 'number'
                        : 'currency'
                    }
                    color={selectedMetric === 'expenses' ? '#DC2626' : '#FD5001'}
                  />
                )}
              </CardContent>
            </Card>
          </FadeInView>

          {/* Saques recentes */}
          {financialSummary && financialSummary.recentPayouts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Saques recentes</CardTitle>
              </CardHeader>
              <CardContent>
                {financialSummary.recentPayouts.slice(0, 5).map((payout, index, arr) => (
                  <View
                    key={payout.id}
                    className={`flex-row items-center gap-3 py-3 ${
                      index < arr.length - 1 ? 'border-b border-neutral-100' : ''
                    }`}
                  >
                    <View className="h-10 w-10 items-center justify-center rounded-full bg-primary-50 dark:bg-primary">
                      <Ionicons
                        className="text-primary dark:text-white"
                        name="arrow-down-circle-outline"
                        size={18}
                      />
                    </View>
                    <View className="flex-1 gap-0.5">
                      <ThemedText
                        className="font-semibold text-sm text-foreground"
                        style={{ fontVariant: ['tabular-nums'] }}
                      >
                        {formatCurrency(payout.amount)}
                      </ThemedText>
                      <ThemedText className="text-xs text-neutral-500">
                        {new Date(payout.arrival_date).toLocaleDateString('pt-BR')}
                      </ThemedText>
                    </View>
                    <View className="rounded-full bg-neutral-100 px-2.5 py-1">
                      <ThemedText className="font-semibold text-xs text-neutral-600">
                        {payout.status}
                      </ThemedText>
                    </View>
                  </View>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {!inTeam && (
        <TransactionList charges={recentCharges} isLoading={isLoadingCharges} limit={20} />
      )}
    </PageLayout>
  );
}
